import Phaser from 'phaser';
import type { CatDefinition, ChartNote, HitResult, JudgmentGrade, Lane, SongDifficulty, SongResult } from '@/types';
import { getSongById } from '@/data/songs';
import { getCatById } from '@/data/cats';
import { getBackgroundById } from '@/data/backgrounds';
import { AudioManager } from '@/game/systems/AudioManager';
import { SaveManager } from '@/game/systems/SaveManager';
import { RhythmEngine } from '@/game/systems/RhythmEngine';
import { InputManager } from '@/game/systems/InputManager';
import { NoteManager } from '@/game/systems/NoteManager';
import { ScoreManager } from '@/game/systems/ScoreManager';
import { DebugOverlay, isDebugMode } from '@/game/systems/DebugOverlay';
import { Cat } from '@/game/objects/Cat';
import { Background } from '@/game/objects/Background';
import { PALETTE, bodyTextStyle, titleTextStyle } from '@/game/ui/theme';
import { spawnBurst, spawnFloatingText, shakeCamera, vibrate } from '@/utils/effects';
import { computeCoinsAwarded, computeStarRating } from '@/utils/scoreMath';
import { CAT_Y, INTERNAL_HEIGHT, INTERNAL_WIDTH } from '@/game/config/GameConfig';

export interface GameplaySceneData {
  songId: string;
  /** Which chart variant to play, for songs with `difficultyCharts`. Ignored (and
   * unnecessary) for songs with only a single fixed chart. */
  difficulty?: SongDifficulty;
}

const GRADE_COLORS: Record<JudgmentGrade, string> = {
  perfect: '#ffe066',
  good: '#8ee3ff',
  miss: '#ff6b6b',
};

const GRADE_LABELS: Record<JudgmentGrade, string> = {
  perfect: 'PERFECT!',
  good: 'Good',
  miss: 'Miss',
};

export class GameplayScene extends Phaser.Scene {
  private audioManager!: AudioManager;
  private saveManager!: SaveManager;
  private rhythmEngine!: RhythmEngine;
  private inputManager!: InputManager;
  private noteManager!: NoteManager;
  private scoreManager!: ScoreManager;
  private debugOverlay?: DebugOverlay;
  private background!: Background;

  private cats!: Record<Lane, Cat>;
  private catDefs!: Record<Lane, CatDefinition>;

  private scoreText!: Phaser.GameObjects.Text;
  private comboText!: Phaser.GameObjects.Text;
  private progressBarFill!: Phaser.GameObjects.Graphics;
  private pauseButton!: Phaser.GameObjects.Container;

  private songId!: string;
  private difficulty?: SongDifficulty;
  /** Key used for high-score/star persistence -- just the songId for single-chart
   * songs, or `${songId}:${difficulty}` for songs with a difficulty picker, so each
   * variant tracks its own best score. */
  private progressKey!: string;
  private ready = false;
  private ended = false;
  /** True for the "Ready?" countdown window between a paid Continue purchase and
   * the song actually resuming: the scene's update/input loop is running again (so
   * cats can be repositioned) but the rhythm engine is still paused, so update()
   * must keep driving cats/input while skipping notes/scoring -- see
   * beginRepositioning(). */
  private repositioning = false;
  /** The song's isolated vocal track (SongMeta.vocalsAudio), if it has one -- catches
   * play a slice of this (the real sung syllable) instead of a synthesized meow. */
  private vocalsBuffer: AudioBuffer | null = null;
  private unsubscribeAutoPause?: () => void;
  /** `?nofail=true` disables the usual any-miss-ends-the-level behavior so a full
   * chart (e.g. an imported real song) can be listened through end to end for
   * testing/tuning without a single miss cutting the run short. */
  private noFailDebug = new URLSearchParams(window.location.search).get('nofail') === 'true';

  constructor() {
    super('GameplayScene');
  }

  init(data: GameplaySceneData): void {
    this.songId = data.songId;
    this.difficulty = data.difficulty;
    this.ready = false;
    this.ended = false;
  }

  create(): void {
    this.audioManager = this.game.registry.get('audioManager');
    this.saveManager = this.game.registry.get('saveManager');

    const song = getSongById(this.songId);
    const chart = song.difficultyCharts?.[this.difficulty ?? 'normal'] ?? song.chart;
    this.progressKey = song.difficultyCharts ? `${song.id}:${this.difficulty ?? 'normal'}` : song.id;
    const backgroundId = this.saveManager.getSelectedBackgroundId() ?? song.background;
    this.background = new Background(this, getBackgroundById(backgroundId));

    this.catDefs = {
      left: getCatById(this.saveManager.getData().selectedLeftCatId),
      right: getCatById(this.saveManager.getData().selectedRightCatId),
    };
    this.cats = {
      left: new Cat(this, 'left', this.catDefs.left, CAT_Y),
      right: new Cat(this, 'right', this.catDefs.right, CAT_Y),
    };

    this.inputManager = new InputManager(this);
    this.scoreManager = new ScoreManager();

    this.buildHud(song.title);

    if (isDebugMode()) {
      this.debugOverlay = new DebugOverlay(this);
    }

    this.audioManager.setMusicVolume(this.saveManager.getSettings().musicVolume);
    this.audioManager.setSfxVolume(this.saveManager.getSettings().sfxVolume);
    this.unsubscribeAutoPause = this.audioManager.onAutoPause(() => this.pauseGame());

    const loadingText = this.add
      .text(INTERNAL_WIDTH / 2, INTERNAL_HEIGHT / 2, 'Loading song...', bodyTextStyle(28))
      .setOrigin(0.5)
      .setDepth(500);

    this.audioManager
      .unlock()
      .then(() => Promise.all([this.audioManager.loadSong(song), this.audioManager.loadVocals(song)]))
      .then(([buffer, vocalsBuffer]) => {
        loadingText.destroy();
        this.vocalsBuffer = vocalsBuffer;
        this.rhythmEngine = new RhythmEngine(this.audioManager, song, chart, buffer);
        this.rhythmEngine.setAudioOffsetMs(this.saveManager.getSettings().audioOffsetMs);
        this.noteManager = new NoteManager(
          this,
          this.rhythmEngine,
          chart,
          this.cats,
          (note, grade, diffMs) => this.handleJudgment(note, grade, diffMs),
          this.saveManager.getData().enabledFoodIds
        );
        this.rhythmEngine.start(song.previewStart ?? 0);
        this.ready = true;
      });

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.cleanup());
  }

  private buildHud(songTitle: string): void {
    this.add
      .text(INTERNAL_WIDTH / 2, 6, songTitle, bodyTextStyle(20, '#cbb9e8'))
      .setOrigin(0.5, 0)
      .setDepth(200);

    this.scoreText = this.add
      .text(INTERNAL_WIDTH / 2, 34, '0', titleTextStyle(44))
      .setOrigin(0.5, 0)
      .setDepth(200);

    this.comboText = this.add
      .text(INTERNAL_WIDTH / 2, 96, '', bodyTextStyle(26, '#ffe066'))
      .setOrigin(0.5, 0)
      .setDepth(200);

    const barBg = this.add.graphics().setDepth(200);
    barBg.fillStyle(0x000000, 0.35);
    barBg.fillRoundedRect(20, 130, INTERNAL_WIDTH - 40, 10, 5);

    this.progressBarFill = this.add.graphics().setDepth(201);

    this.pauseButton = this.add.container(INTERNAL_WIDTH - 44, 40).setDepth(200);
    const pauseBg = this.add.circle(0, 0, 26, 0x000000, 0.35);
    const pauseIcon = this.add.text(0, 0, '❚❚', bodyTextStyle(20)).setOrigin(0.5);
    this.pauseButton.add([pauseBg, pauseIcon]);
    this.pauseButton.setSize(52, 52);
    this.pauseButton.setInteractive({ useHandCursor: true });
    this.pauseButton.on('pointerdown', () => this.pauseGame());
  }

  private handleJudgment(note: ChartNote, grade: JudgmentGrade, diffMs: number): void {
    if (grade === 'miss') {
      // A chart pattern can drop notes in both lanes at once, so NoteManager may
      // resolve two misses in the same update(). Register this miss into the score
      // *before* failLevel() -- which captures a snapshot for the Results screen --
      // only when the level hasn't already ended, so a same-frame second miss
      // doesn't get double-counted or (worse) overwrite the snapshot after the fact.
      // failLevel() itself always runs so this lane's cat reacts either way; it only
      // performs the pause/results transition once.
      if (!this.ended) {
        const result = this.scoreManager.registerJudgment(note, grade, diffMs);
        this.refreshHudText(result);
      }
      if (!this.noFailDebug) this.failLevel(note.lane);
      return;
    }

    if (this.ended) return;
    const result: HitResult = this.scoreManager.registerJudgment(note, grade, diffMs);

    const cat = this.cats[note.lane];
    const zone = cat.getCatchZone();
    cat.playEat();
    const vocalVelocity = grade === 'perfect' ? 1 : 0.75;
    if (this.vocalsBuffer && note.vocalDuration !== undefined) {
      this.audioManager.playVocalClip(this.vocalsBuffer, note.time, note.vocalDuration, vocalVelocity);
    } else if (note.pitch !== undefined) {
      this.audioManager.playCatVocal(this.catDefs[note.lane], note.pitch, vocalVelocity);
    }
    spawnBurst(this, zone.x, zone.y, grade === 'perfect' ? PALETTE.accent : PALETTE.secondary, grade === 'perfect' ? 16 : 10);
    spawnFloatingText(this, zone.x, zone.y, GRADE_LABELS[grade], GRADE_COLORS[grade], grade === 'perfect' ? 32 : 24);
    vibrate(grade === 'perfect' ? 15 : 8);

    const snapshot = this.scoreManager.getSnapshot();
    if (snapshot.combo > 0 && snapshot.combo % 25 === 0) {
      cat.playComboCelebration();
      this.cats[note.lane === 'left' ? 'right' : 'left'].playComboCelebration();
      spawnFloatingText(this, INTERNAL_WIDTH / 2, INTERNAL_HEIGHT * 0.4, `${snapshot.combo} COMBO!`, '#ffe066', 40);
      shakeCamera(this, 0.004, 180);
      vibrate([20, 30, 20]);
    }

    this.refreshHudText(result);
  }

  private refreshHudText(result: HitResult): void {
    const snapshot = this.scoreManager.getSnapshot();
    this.scoreText.setText(snapshot.score.toLocaleString());
    this.comboText.setText(snapshot.combo > 1 ? `${snapshot.combo}x combo` : '');
    void result;
  }

  update(_time: number, delta: number): void {
    const deltaSeconds = delta / 1000;
    this.background.update(deltaSeconds);

    if (!this.ready) return;

    this.inputManager.update(deltaSeconds);
    this.cats.left.update(this.inputManager.getTargetX('left'), deltaSeconds);
    this.cats.right.update(this.inputManager.getTargetX('right'), deltaSeconds);
    if (this.repositioning) return;
    this.noteManager.update();

    const progress = this.rhythmEngine.songProgress;
    this.progressBarFill.clear();
    this.progressBarFill.fillStyle(PALETTE.primary, 1);
    this.progressBarFill.fillRoundedRect(20, 130, (INTERNAL_WIDTH - 40) * progress, 10, 5);

    if (this.debugOverlay) {
      this.debugOverlay.update({
        songTime: this.rhythmEngine.currentSongTime,
        beat: this.rhythmEngine.currentBeat,
        bpm: this.rhythmEngine.bpm,
        fps: this.game.loop.actualFps,
        audioOffsetMs: this.saveManager.getSettings().audioOffsetMs,
        activeNotes: this.noteManager.activeCount(),
        songProgress: progress,
      });
    }

    if (!this.ended && this.rhythmEngine.isFinished() && this.noteManager.isComplete()) {
      this.endSong();
    }
  }

  getSongId(): string {
    return this.songId;
  }

  resumeFromPause(): void {
    this.rhythmEngine.resume();
    this.scene.resume();
  }

  /** Called by ResultsScene as soon as a paid Continue is purchased, before its
   * "Ready?" countdown starts counting down. Resumes this scene's own update/input
   * loop -- paused since failLevel() -- so the player can drag their cats back into
   * position while ResultsScene's countdown overlay plays on top, but leaves the
   * rhythm engine (and therefore notes/scoring/song time) paused throughout: `ended`
   * stays true and update() bails out before touching NoteManager while
   * `repositioning` is set, so nothing can be judged until the countdown actually
   * hands off to continueAfterPayment(). Also clears the failing cat's cry so it
   * doesn't sit there tinted and drooping while the player gets ready. */
  beginRepositioning(): void {
    this.repositioning = true;
    this.cats.left.resetToIdle();
    this.cats.right.resetToIdle();
    this.scene.resume();
  }

  /** Called by ResultsScene once a paid Continue's countdown finishes. Undoes
   * failLevel()'s "this run is over" state (it only paused, never stopped, the
   * rhythm engine specifically so this could resume from the exact failure moment
   * instead of restarting the song) and clears the failing cat's cry visual. */
  continueAfterPayment(): void {
    this.ended = false;
    this.repositioning = false;
    this.cats.left.resetToIdle();
    this.cats.right.resetToIdle();
    this.resumeFromPause();
  }

  private pauseGame(): void {
    if (this.ended || !this.ready) return;
    this.rhythmEngine.pause();
    this.scene.pause();
    this.scene.launch('PauseScene', { gameplaySceneKey: this.scene.key });
  }

  private endSong(): void {
    this.ended = true;
    this.rhythmEngine.markFinished();
    const snapshot = this.scoreManager.getSnapshot();
    const stars = computeStarRating(snapshot.accuracy);
    const coins = computeCoinsAwarded(snapshot.score, stars);
    const isNewHighScore = this.saveManager.recordSongResult(this.progressKey, snapshot.score, stars);
    this.saveManager.addCoins(coins);

    const result: SongResult = {
      ...snapshot,
      songId: this.songId,
      difficulty: this.difficulty,
      stars,
      isNewHighScore,
      coinsAwarded: coins,
      failed: false,
    };

    this.time.delayedCall(600, () => {
      this.scene.start('ResultsScene', { result });
    });
  }

  /** Any missed treat ends the run immediately -- no continuing with a broken combo,
   * unless the player pays to continue from Results. Pauses (rather than stops) the
   * song so that a paid continue can resume from this exact moment instead of
   * restarting the whole chart; ResultsScene is launched as an overlay on top of this
   * still-paused scene (mirroring PauseScene) rather than replacing it, precisely so
   * that resuming is possible at all. Plays the splat sting and shows the failing cat
   * crying before handing off to a Results screen with no stars/coins/high-score.
   *
   * The cry always plays for whichever lane just missed, even if the level already
   * ended this same frame -- NoteManager can resolve both lanes' misses in one
   * update() when a chart pattern drops two notes at once, and without this the
   * `ended` guard below (needed so the pause/results transition only ever fires
   * once) would silently swallow the second lane's cat ever showing its cry. */
  private failLevel(lane: Lane): void {
    const cat = this.cats[lane];
    const zone = cat.getCatchZone();
    cat.playCry();
    spawnFloatingText(this, zone.x, zone.y, 'SPLAT!', GRADE_COLORS.miss, 34);

    if (this.ended) return;
    this.ended = true;
    this.rhythmEngine.pause();

    this.audioManager.playSplat();
    shakeCamera(this, 0.012, 320);
    vibrate([30, 40, 30, 60]);

    const snapshot = this.scoreManager.getSnapshot();
    const result: SongResult = {
      ...snapshot,
      songId: this.songId,
      difficulty: this.difficulty,
      stars: 1,
      isNewHighScore: false,
      coinsAwarded: 0,
      failed: true,
    };

    this.time.delayedCall(1200, () => {
      this.scene.pause();
      this.scene.launch('ResultsScene', { result, gameplaySceneKey: this.scene.key });
    });
  }

  private cleanup(): void {
    this.inputManager?.destroy();
    this.noteManager?.destroy();
    this.debugOverlay?.destroy();
    this.rhythmEngine?.stop();
    this.unsubscribeAutoPause?.();
  }
}
